import type { CharacterDraft } from "@/features/characters/store"
import { isPristineCharacterDraft } from "@/features/create/lib/random-character-defaults"

export type CreateWizardPhase = "form" | "generating" | "signup_gate" | "created"

export interface CreateWizardSession {
  version: 2
  sessionId: string
  savedAt: number
  draft: CharacterDraft
  currentStep: number
  maxVisitedStep: number
  phase: CreateWizardPhase
  showSignupCTA: boolean
  createdCharacterId: string | null
  /** True when the user hit the signup gate and should resume generation after auth. */
  pendingResumeGeneration: boolean
}

const STORAGE_KEY = "pleasurai:create-wizard-session:v1"
const SESSION_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000
const VALID_PHASES: CreateWizardPhase[] = [
  "form",
  "generating",
  "signup_gate",
  "created",
]

function isBrowser(): boolean {
  return typeof window !== "undefined"
}

function removeStoredSession(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Storage can be blocked (SecurityError in some private modes / iframes).
  }
}

function isValidSession(value: unknown): value is CreateWizardSession {
  if (!value || typeof value !== "object") return false
  const session = value as CreateWizardSession
  return (
    session.version === 2 &&
    typeof session.sessionId === "string" &&
    typeof session.savedAt === "number" &&
    typeof session.draft === "object" &&
    session.draft !== null &&
    typeof session.currentStep === "number" &&
    typeof session.maxVisitedStep === "number" &&
    typeof session.showSignupCTA === "boolean" &&
    (session.createdCharacterId === null ||
      typeof session.createdCharacterId === "string") &&
    VALID_PHASES.includes(session.phase)
  )
}

export function loadCreateWizardSession(): CreateWizardSession | null {
  if (!isBrowser()) return null

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null

    const parsed: unknown = JSON.parse(raw)
    if (!isValidSession(parsed)) {
      removeStoredSession()
      return null
    }

    if (Date.now() - parsed.savedAt > SESSION_MAX_AGE_MS) {
      removeStoredSession()
      return null
    }

    return parsed
  } catch {
    removeStoredSession()
    return null
  }
}

export function saveCreateWizardSession(session: CreateWizardSession): void {
  if (!isBrowser()) return

  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...session, savedAt: Date.now() })
    )
  } catch {
    // Ignore quota / private-mode errors — in-memory flow still works.
  }
}

export function clearCreateWizardSession(): void {
  if (!isBrowser()) return
  removeStoredSession()
}

/** Only restore sessions with real in-progress or completed work — not empty stale blobs. */
export function shouldRestoreCreateSession(
  session: CreateWizardSession
): boolean {
  if (session.createdCharacterId) return false
  if (session.phase === "created") return false
  if (session.showSignupCTA) return true
  if (session.phase === "signup_gate" || session.phase === "generating") {
    return true
  }
  if (session.maxVisitedStep > 0) return true
  if (!isPristineCharacterDraft(session.draft)) return true
  return false
}
